import { Inject, Injectable, Optional } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { ConcurrentUsageService } from '../../services/concurrent-usage/concurrent-usage.service';
import { compressString, decompressString } from '../../utilities/compression-utilities';
import { hydrateSlice, persistenceError, persistSlice, slicePersisted, sliceHydrated } from './persistence.actions';
import { PERSISTED_SLICES, PersistedSlice, persistedSliceStorageKey } from './persistence.registry';

/**
 * Owns the generic slice-persistence side effects. Slices register a projection selector via
 * {@link PERSISTED_SLICES}; `persist$` gzip-compresses that projection and writes the bytes to
 * the concurrency store on `persistSlice`, and `hydrate$` reads and decompresses them on
 * `hydrateSlice`. Routing through the concurrency store (the same key-value store the timesheet
 * uses) keeps persisted slices in the cross-device concurrent-usage sync path, unless a slice is
 * registered `localOnly` (see `PersistedSlice`). The compressed payload is base64-encoded exactly
 * once, for both local storage and upload (see `ConcurrentUsageService.writeBinaryDataForKey`).
 * The NgRx store is the source of truth: storage is written on save and read only to rehydrate.
 */
@Injectable()
export class PersistenceEffects {
    private readonly registry = new Map<string, PersistedSlice>();

    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly concurrencyStore: ConcurrentUsageService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Optional() @Inject(PERSISTED_SLICES) registeredSlices: PersistedSlice[] | null,
    ) {
        (registeredSlices ?? []).forEach((slice) => this.registry.set(slice.featureKey, slice));
    }

    public persist$ = createEffect(() =>
        this.actions$.pipe(
            ofType(persistSlice),
            mergeMap(({ featureKey, correlationId }) => {
                const slice = this.registry.get(featureKey);
                if (!slice) {
                    this.log.error(`PersistenceEffects: persist$: no slice registered for '${featureKey}'`);
                    return of(persistenceError({ featureKey, error: 'not registered', correlationId }));
                }
                return this.store.select(slice.persistSelector).pipe(
                    take(1),
                    // Serialize and gzip-compress the projection, then write the bytes and stamp a
                    // write timestamp so it participates in concurrent-usage sync.
                    switchMap((data) => from(compressString(JSON.stringify(data)))),
                    switchMap((bytes) =>
                        this.concurrencyStore.writeBinaryDataForKey(
                            persistedSliceStorageKey(featureKey),
                            bytes,
                            Date.now(),
                        ),
                    ),
                    map(() => slicePersisted({ featureKey, correlationId })),
                    catchError((error) => {
                        this.log.error(`PersistenceEffects: persist$: ${featureKey}: ${error}`);
                        return of(persistenceError({ featureKey, error: String(error), correlationId }));
                    }),
                );
            }),
        ),
    );

    public hydrate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(hydrateSlice),
            mergeMap(({ featureKey }) => {
                const slice = this.registry.get(featureKey);
                if (!slice) {
                    this.log.error(`PersistenceEffects: hydrate$: no slice registered for '${featureKey}'`);
                    return of(sliceHydrated({ featureKey, data: undefined }));
                }
                return this.concurrencyStore.readBinaryDataForKey(persistedSliceStorageKey(featureKey)).pipe(
                    // `readBinaryDataForKey` yields an empty array when nothing is stored; otherwise
                    // decompress and deserialize the bytes back into the slice's projection.
                    switchMap(([compressed]) => (compressed ? from(decompressString(compressed)) : of(undefined))),
                    map((json) =>
                        sliceHydrated({ featureKey, data: json !== undefined ? JSON.parse(json) : undefined }),
                    ),
                    catchError((error) => {
                        // A read/decompress/parse failure is treated as nothing to hydrate — hydrate
                        // with `undefined` so the slice keeps its initial state.
                        this.log.debug(`PersistenceEffects: hydrate$: ${featureKey}: nothing to hydrate (${error})`);
                        return of(sliceHydrated({ featureKey, data: undefined }));
                    }),
                );
            }),
        ),
    );
}
